import { useState } from 'react';

import produce, { enableMapSet } from 'immer';
import create from 'zustand';

import { TableStore } from '@components/table/table-store';
import { ImmerUpdateFn } from '@utils/types';

import { parseActionIdFromColumnId } from './columns';
import { ExtraStateStore } from './extra-state-store';

enableMapSet();

export const FOCUS_FLASH_MS = 100;

type TravellersColumnControlStoreState = {
  isOpen: boolean;
  expandedActionIds: Set<string>;
  focusedVariableId: string;
};

export const createTravellersColumnControlStore = (
  tableStore: TableStore,
  extraStateStore: ExtraStateStore,
  onFieldsNeeded?: (newFieldIds: string[]) => void
) => {
  const useStore = create<TravellersColumnControlStoreState>(() => ({
    isOpen: false,
    expandedActionIds: new Set(),
    focusedVariableId: '',
  }));

  const update = (fn: ImmerUpdateFn<TravellersColumnControlStoreState>) => {
    return useStore.setState((state) => produce(state, fn));
  };

  let focusResetTimer: ReturnType<typeof setTimeout> | undefined;

  const clearFocusResetTimer = () => {
    if (focusResetTimer !== undefined) {
      clearTimeout(focusResetTimer);
      focusResetTimer = undefined;
    }
  };

  const setOpenness = (value: boolean) => {
    update((state) => {
      state.isOpen = value;
      if (!value) {
        state.expandedActionIds = new Set();
        state.focusedVariableId = '';
      }
    });

    if (!value) {
      clearFocusResetTimer();
      tableStore.actions.persistImmediate();
    }
  };

  const close = () => {
    setOpenness(false);
  };

  const open = () => {
    setOpenness(true);
  };

  const toggleColumn = (id: string, value: boolean) => {
    if (value) {
      tableStore.actions.addColumn(id);
      extraStateStore.actions.addActionId(id, onFieldsNeeded);
    } else {
      tableStore.actions.removeColumn(id);
      extraStateStore.actions.removeActionId(id);
    }
    tableStore.actions.persistImmediate();
  };

  const toggleExpanded = (actionId: string) => {
    update((state) => {
      if (state.expandedActionIds.has(actionId)) {
        state.expandedActionIds.delete(actionId);
      } else {
        state.expandedActionIds.add(actionId);
      }
    });
  };

  const setExpanded = (actionIds: Set<string>) => {
    update((state) => {
      state.expandedActionIds = actionIds;
    });
  };

  const focusColumn = (columnId: string) => {
    const actionId = parseActionIdFromColumnId(columnId);
    if (!actionId) {
      return;
    }
    update((state) => {
      state.isOpen = true;
      state.expandedActionIds.add(actionId);
      state.focusedVariableId = columnId;
    });

    clearFocusResetTimer();
    focusResetTimer = setTimeout(() => {
      focusResetTimer = undefined;
      update((state) => {
        state.focusedVariableId = '';
      });
    }, FOCUS_FLASH_MS);
  };

  const useIsOpen = () => useStore((state) => state.isOpen);
  const useFocusedVariableId = () => useStore((state) => state.focusedVariableId);
  const useExpandedActionIds = () => useStore((state) => state.expandedActionIds);

  return {
    useIsOpen,
    useFocusedVariableId,
    useExpandedActionIds,
    actions: {
      getState: useStore.getState,
      setOpenness,
      toggleColumn,
      toggleExpanded,
      setExpanded,
      focusColumn,
      close,
      open,
    },
  };
};

export const useTravellersColumnControlStore = (
  tableStore: TableStore,
  extraStateStore: ExtraStateStore,
  onFieldsNeeded?: (newFieldIds: string[]) => void
) => {
  return useState(() =>
    createTravellersColumnControlStore(tableStore, extraStateStore, onFieldsNeeded)
  )[0];
};

export type TravellersColumnControlStore = ReturnType<typeof useTravellersColumnControlStore>;

export type TravellersColumnControlActions = TravellersColumnControlStore['actions'];
